import { TFunction } from 'i18next';
import {
  StartOnEventType,
  StartOnStepValue,
  StepResult,
  WorkflowStepFormData,
} from '../data/workflow-form.model';
import { customInput } from './workflowUtils';
import {
  WorkflowActionType,
  WorkflowAlert,
} from '../data/models/workflow.model';
import capitalize from 'lodash/capitalize';
import lowerFirst from 'lodash/lowerFirst';

export type StatusWorkflowActionType =
  | WorkflowActionType.Succeed
  | WorkflowActionType.Fail
  | WorkflowActionType.Cancel;

class DescribeWorkflowUtils {
  /**
   * Given a workflow step, returns a description of the step
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns a list of descriptions for this step
   */
  public getStepDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string[] {
    const descriptions = this.getStartOnDescription(
      step,
      otherSteps,
      translateFunction,
    );
    const actionDesc = this.getActionDescription(
      step,
      otherSteps,
      translateFunction,
    );
    if (actionDesc) {
      descriptions.push(actionDesc);
    }
    return descriptions;
  }

  /**
   * Given a workflow step, returns a description for any elements
   * in the step's "starton" array.
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns a list of descriptions
   */
  private getStartOnDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string[] {
    const descriptions: string[] = [];
    const eventDesc = this.getStartOnEventDescription(step, translateFunction);
    if (eventDesc) {
      descriptions.push(eventDesc);
    }
    const stepDesc = this.getStartOnStepDescription(
      step,
      otherSteps,
      translateFunction,
    );
    if (stepDesc) {
      descriptions.push(stepDesc);
    }
    return descriptions;
  }

  /**
   * Given a workflow step, returns a description for any elements
   * in the step's "starton" that are of type "event"
   *
   * @param step the step to get a description of
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getStartOnEventDescription(
    step: WorkflowStepFormData,
    translateFunction: TFunction,
  ): string {
    const eventNames = step.startOn
      .filter((startOn) => startOn.startOnType === StartOnEventType.Event)
      .map((startOn) => {
        const value =
          startOn.startOnValue === customInput
            ? startOn.startOnCustomValue
            : startOn.startOnValue;
        return value ?? '';
      })
      .filter((value) => !!value);

    if (eventNames.length === 0) {
      return '';
    }

    return `${translateFunction('workflows.when')} ${this.formatList(eventNames, 'or', translateFunction)}`;
  }

  /**
   * Given a workflow step, returns a description for any elements
   * in the step's "starton" that are of type "step"
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getStartOnStepDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    const startOnStepDescriptions = step.startOn
      .filter((startOn) => startOn.startOnType === StartOnEventType.Step)
      .map((startOn) => {
        try {
          const startOnStepValue = JSON.parse(
            startOn.startOnValue,
          ) as StartOnStepValue;
          const startStepName = otherSteps.find(
            (s) => s.tempId === startOnStepValue.stepId,
          )?.name;
          return `${startStepName} ${startOnStepValue.status}`;
        } catch (error) {
          return '';
        }
      })
      .filter((value) => !!value);
    if (startOnStepDescriptions.length === 0) {
      return '';
    }
    return `${translateFunction('workflows.when')} ${this.formatList(startOnStepDescriptions, 'or', translateFunction)}`;
  }

  /**
   * Given a workflow step, returns a description for the step's action
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getActionDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    if (!step.action) {
      return '';
    }
    switch (step.action) {
      case WorkflowActionType.Succeed:
      case WorkflowActionType.Fail:
      case WorkflowActionType.Cancel:
        return this.getStatusActionDescription(
          step,
          otherSteps,
          translateFunction,
        );
      case WorkflowActionType.Wait:
        return this.getWaitActionDescription(
          step,
          otherSteps,
          translateFunction,
        );
      case WorkflowActionType.PublishAlert:
      case WorkflowActionType.TaskHuman:
        return this.getAlertActionDescription(
          step,
          otherSteps,
          translateFunction,
        );
      case WorkflowActionType.PublishEvent:
        return this.getPublishEventActionDescription(
          step,
          otherSteps,
          translateFunction,
        );
      case WorkflowActionType.RejectTripLeg:
        return ''; //TODO
    }
  }

  /**
   * Given a workflow step, returns a description for the
   * step's publish event action
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getPublishEventActionDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    if (step.action !== WorkflowActionType.PublishEvent || !step.publishEvent) {
      return '';
    }
    if (!step.publishEvent.name || !step.publishEvent.detailSource) {
      return '';
    }
    const translateParams = {
      name: step.publishEvent.name,
      source: translateFunction(
        `workflows.${lowerFirst(step.publishEvent.detailSource)}`,
      ),
    };
    const desc = translateFunction(
      'workflows.publishEventDesc',
      translateParams,
    );
    return this.addTransitionToDesc(desc, step, otherSteps, translateFunction);
  }

  /**
   * Given a workflow step, returns a description for the
   * step's publish alert/task human action
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getAlertActionDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    if (
      step.action !== WorkflowActionType.PublishAlert &&
      step.action !== WorkflowActionType.TaskHuman
    ) {
      return '';
    }
    let alert: WorkflowAlert | undefined;
    if (step.action === WorkflowActionType.PublishAlert) {
      alert = step.publishAlert;
    }
    if (step.action === WorkflowActionType.TaskHuman) {
      alert = step.taskHuman;
    }
    if (!alert || !alert.alertTypeBC || !alert.alertEventBC) {
      return '';
    }
    const translateParams = {
      type: alert.alertTypeBC,
      code: alert.alertEventBC,
      recipient: alert.alertRecipientBC,
    };
    const translationKey = alert.alertRecipientBC
      ? `workflows.${step.action}WithRecipientDesc`
      : `workflows.${step.action}Desc`;
    const desc = translateFunction(translationKey, translateParams);
    return this.addTransitionToDesc(desc, step, otherSteps, translateFunction);
  }

  /**
   * Given a workflow step, returns a description for the
   * step's wait action
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getWaitActionDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    if (step.action !== WorkflowActionType.Wait || !step.wait) {
      return '';
    }
    const { waitAmount, waitAmountUnit, waitTimePeriod, waitBaseDateTimeRef } =
      step.wait;
    if (
      !waitAmount ||
      !waitAmountUnit ||
      !waitTimePeriod ||
      !waitBaseDateTimeRef
    ) {
      return '';
    }
    const unit = this.getFormattedWaitAmountUnit(waitAmount, waitAmountUnit);

    let desc = translateFunction('workflows.waitDesc', {
      amount: waitAmount,
      unit,
      timePeriod: waitTimePeriod,
      ref: waitBaseDateTimeRef,
    });

    return this.addTransitionToDesc(desc, step, otherSteps, translateFunction);
  }

  /**
   * Adds a step transitions description to an existing description
   * @param description the existing description
   * @param step the step for the existing description
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private addTransitionToDesc(
    description: string,
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    let transitionDescriptions: string[] = [];
    [
      WorkflowActionType.Succeed,
      WorkflowActionType.Fail,
      WorkflowActionType.Cancel,
    ].forEach((actionType) => {
      const transitionStep = this.getTransitionStepForActionType(
        actionType as StatusWorkflowActionType,
        step,
        otherSteps,
      );
      if (transitionStep) {
        const desc = translateFunction(`workflows.${actionType}Transition`, {
          stepName: transitionStep.name,
        });
        transitionDescriptions.push(desc);
      }
    });
    if (transitionDescriptions.length === 0) {
      return description;
    } else {
      return `${description} ${translateFunction('workflows.then')} ${this.formatList(transitionDescriptions, 'and', translateFunction)}`;
    }
  }

  /**
   * Given a workflow step, returns a description for a
   * status action (succeed/fail/cancel)
   *
   * @param step the step to get a description of
   * @param otherSteps all other steps
   * @param translateFunction i18next translate function
   * @returns the description
   */
  private getStatusActionDescription(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
    translateFunction: TFunction,
  ): string {
    const action = step.action;
    if (
      action === WorkflowActionType.Succeed ||
      action === WorkflowActionType.Fail ||
      action === WorkflowActionType.Cancel
    ) {
      const transitionStep = this.getTransitionStepForActionType(
        action,
        step,
        otherSteps,
      );

      // If there is no transition to another step on succeed/fail/cancel
      // then the workflow ends.
      if (transitionStep) {
        return translateFunction('workflows.actionStepAndGoTo', {
          action: capitalize(action),
          stepName: transitionStep.name,
        });
      } else {
        return translateFunction(`workflows.${action}Workflow`);
      }
    }
    return '';
  }

  /**
   * Returns the "next" step for a given step and action type
   * @param actionType the action type
   * @param step the step
   * @param otherSteps all other steps
   * @returns the "next" step
   */
  public getTransitionStepForActionType(
    actionType: StatusWorkflowActionType,
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
  ): WorkflowStepFormData | undefined {
    let stepResult: StepResult;
    switch (actionType) {
      case WorkflowActionType.Succeed:
        stepResult = StepResult.Success;
        break;
      case WorkflowActionType.Fail:
        stepResult = StepResult.Failure;
        break;
      case WorkflowActionType.Cancel:
        stepResult = StepResult.Canceled;
        break;
    }
    return otherSteps.find((s) => {
      return !!s.startOn.find((startOn) => {
        if (startOn.startOnType === StartOnEventType.Step) {
          try {
            const stepValue = JSON.parse(
              startOn.startOnValue,
            ) as StartOnStepValue;
            if (
              stepValue.status === stepResult &&
              stepValue.stepId === step.tempId
            ) {
              return true;
            }
          } catch (error) {
            return false;
          }
        }
        return false;
      });
    });
  }

  public getFormattedWaitAmountUnit(
    waitAmount: string,
    waitAmountUnit: string,
  ) {
    const waitAmountInt = parseInt(waitAmount);
    return waitAmountInt === 1 ? waitAmountUnit.slice(0, -1) : waitAmountUnit;
  }

  /**
   * Formats an array of strings into a human readable list
   * @param items an array of strings
   * @param conjunction and/or
   * @param translateFunction i18next translate function
   * @returns a human readable list
   */
  private formatList(
    items: string[],
    conjunction: 'and' | 'or',
    translateFunction: TFunction,
  ): string {
    if (items.length === 0) {
      return '';
    } else if (items.length === 1) {
      return items[0];
    } else if (items.length === 2) {
      return `${items[0]} ${translateFunction(`workflows.${conjunction}`)} ${items[1]}`;
    } else {
      const allButLast = items.slice(0, -1).join(', ');
      const last = items[items.length - 1];
      return `${allButLast} ${translateFunction(`workflows.${conjunction}`)} ${last}`;
    }
  }
}

const describeWorkflowUtils = new DescribeWorkflowUtils();

export default describeWorkflowUtils;
